"use client";
import Icon from "@/components/icons/Icons";
import { apiPost } from "@/utils/apiClient";
import { motion } from "framer-motion";
import React from "react";
import { useState, useEffect, useRef } from "react";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { useLoginModal } from "@/utils/useLoginModal";
import { useDispatch } from "react-redux";
import { AnimatePresence } from "framer-motion";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";
import { useRouter } from "next/navigation";
import {
  PortfolioApiOption,
  PortfolioApiQuestion,
  PortfolioAnswerItem,
} from "@/type/servicesPageTypes";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import Image from "next/image";
import { addToast } from "@/components/ui/CustomToastManager";

type PortfolioModelProps = {
  onClose: () => void;
  url: string;
  quesData: PortfolioApiQuestion[];
};
const PortfolioModel: React.FC<PortfolioModelProps> = ({
  onClose,
  url,
  quesData,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [questions, setQuestions] = useState<PortfolioApiQuestion[]>([]);
  const [groupedQuestions, setGroupedQuestions] = useState<
    Record<string, PortfolioApiQuestion[]>
  >({});
  const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const [showSteps, setShowSteps] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const [isLoading, setIsLoading] = useState(true);
  const [errorQuestions, setErrorQuestions] = useState<Set<number>>(new Set());
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [amountErrors, setAmountErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 4;
    setAtBottom(isAtBottom);
  };

  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollToQuestion = (index: number) => {
    const nextQuestion = visibleQuestions[index];
    if (nextQuestion && questionRefs.current[nextQuestion.id]) {
      questionRefs.current[nextQuestion.id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  const isButtonDisabled =
    loading || Object.keys(amountErrors).length > 0 || errorQuestions.size > 0;
  const scrollToNextUnanswered = (
    currentIndex: number,
    justAnsweredId: number,
  ) => {
    if (errorQuestions.size > 0) {
      // Find the next question that still has an error (excluding the one just answered)
      const nextErrored = visibleQuestions.find(
        (q) => errorQuestions.has(q.id) && q.id !== justAnsweredId,
      );
      if (nextErrored) {
        const idx = currentQuestions.indexOf(nextErrored);
        scrollToQuestion(idx);
      }
      // If no more errored questions remain, don't scroll anywhere
    } else {
      scrollToQuestion(currentIndex + 1);
    }
  };

  useEffect(() => {
    const checkScreen = () => {
      setIsDesktop(window.innerWidth < 883);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);

    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  useEffect(() => {
    if (quesData && Array.isArray(quesData)) {
      setQuestions(quesData);
      const grouped = quesData.reduce(
        (
          acc: Record<string, PortfolioApiQuestion[]>,
          item: PortfolioApiQuestion,
        ) => {
          const key = item.stepper_title || "Untitled";
          (acc[key] = acc[key] || []).push(item);
          return acc;
        },
        {},
      );
      setGroupedQuestions(grouped);
      setIsLoading(false);
    } else if (quesData) {
      setIsLoading(false);
    }
  }, [quesData]);

  const isStepCompleted = (questions: PortfolioApiQuestion[]) => {
    const filtered = questions.filter((q) => {
      if (q.id === 143) return false;
      if (q.id === SIP_Q_ID)
        return (
          selectedInvestmentOption === "SIP" ||
          selectedInvestmentOption === "SIP + Lumpsum"
        );
      if (q.id === LUMPSUM_Q_ID)
        return (
          selectedInvestmentOption === "Lumpsum" ||
          selectedInvestmentOption === "SIP + Lumpsum"
        );
      return true;
    });
    return filtered.every((q) => !!answers[q.id]);
  };

  const submitFeedback = async () => {
    setLoading(true);
    try {
      const answerList: PortfolioAnswerItem[] = Object.values(answers);

      const payload = {
        subject: "Client Assessment 2.o",
        answerList,
        device: "Web",
        schemeData: null,
      };

      const res = await apiPost("feedback/submitFeedback", payload);
      if (!res?.status) {
        throw new Error(res?.message || "Something went wrong");
      }
      router.push(`/portfolio-services/${url}`);
    } catch (error) {
      setLoading(false);
      addToast({
        title: "Submission Failed",
        description: "Something went wrong while submitting feedback.",
        status: "failed",
      });
      console.error(error || "Something went wrong");
    }
  };

  useEffect(() => {
    if (isLoggedIn && pendingSubmit) {
      setPendingSubmit(false);
      submitFeedback();
    }
  }, [isLoggedIn, pendingSubmit]);

  const nextStep = async () => {
    const firstUnanswered = visibleQuestions.find(
      (q) => !answers[q.id] && q.id !== 143,
    );
    if (firstUnanswered) {
      setErrorQuestions(new Set([firstUnanswered.id]));
      const idx = currentQuestions.indexOf(firstUnanswered);
      scrollToQuestion(idx);
      return;
    }

    setErrorQuestions(new Set());
    if (currentStep === totalSteps) {
      if (!isLoggedIn) {
        setPendingSubmit(true);
        open();
        setModalKey(`${url}-modal`);
        return;
      }

      await submitFeedback();
      return;
    }

    if (currentStep < totalSteps) {
      setActiveQuestionIndex(0);
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setErrorQuestions(new Set());
      setActiveQuestionIndex(0);
      setCurrentStep((prev) => prev - 1);
    }
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<{
    [key: number]: {
      question_id: number;
      option_id: number;
      answer: string;
    };
  }>({});

  useEffect(() => {
    handleScroll();
  }, [questions, currentStep]);

  const groupedArray = Object.entries(groupedQuestions);

  const totalSteps = groupedArray.length;

  const currentGroup = groupedArray[currentStep - 1];

  const currentStepperTitle = currentGroup?.[0] || "";

  // Add this right after the currentQuestions line
  const currentQuestions = currentGroup?.[1] || [];

  // ADD THIS:
  const INVESTMENT_TYPE_Q_ID = 140;
  const SIP_Q_ID = 141;
  const LUMPSUM_Q_ID = 142;

  const selectedInvestmentOption = answers[INVESTMENT_TYPE_Q_ID]?.answer ?? "";

  const visibleQuestions = currentQuestions.filter((q) => {
    if (q.id === SIP_Q_ID) {
      return (
        selectedInvestmentOption === "SIP" ||
        selectedInvestmentOption === "SIP + Lumpsum"
      );
    }
    if (q.id === LUMPSUM_Q_ID) {
      return (
        selectedInvestmentOption === "Lumpsum" ||
        selectedInvestmentOption === "SIP + Lumpsum"
      );
    }
    return true;
  });

  useEffect(() => {
    const timer = setTimeout(() => handleScroll(), 100);
    return () => clearTimeout(timer);
  }, [questions, currentStep]);

  return (
    <div>
      {isLoading ? (
        <div className="fixed inset-0 z-5001 flex items-center justify-center backdrop-blur-[20px] bg-(--primary)">
          <div className="w-full max-w-6xl flex flex-col 2md:flex-row gap-6 2md:gap-20 px-4">
            {/* Left sidebar skeleton */}
            <div className="w-full 2md:w-125.75 p-6 hidden md:flex flex-col gap-4">
              <div className="h-8 w-48 bg-white/20 rounded-lg animate-pulse mb-4" />
              <div className="h-px w-full bg-white/30 mb-4" />
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 px-4 py-3 rounded-lg bg-white/10 animate-pulse"
                >
                  <div className="w-8 h-8 rounded-full bg-white/20 shrink-0" />
                  <div className="h-4 bg-white/20 rounded-sm w-3/4" />
                </div>
              ))}
            </div>

            {/* Right content skeleton */}
            <div className="w-full flex flex-col gap-4 min-h-136">
              {/* Progress bar mobile skeleton */}
              <div className="md:hidden h-8 w-full bg-white/20 rounded-full animate-pulse" />

              {/* Card skeleton */}
              <div className="bg-white rounded-8xl p-6 w-full h-136">
                {/* Step label */}
                <div className="h-3 w-24 bg-gray-200 rounded-sm animate-pulse mb-4" />
                {/* Question */}
                <div className="h-6 w-3/4 bg-gray-200 rounded-sm animate-pulse mb-6" />

                {/* Options */}
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-4 py-4 rounded-xl border border-gray-200 mb-3 animate-pulse"
                  >
                    <div className="w-4 h-4 rounded-full bg-gray-200 shrink-0" />
                    <div className="h-4 bg-gray-200 rounded-sm w-2/3" />
                  </div>
                ))}
              </div>

              {/* Button skeleton */}
              <div className="flex justify-center mt-2">
                <div className="h-10 w-60.5 2md:w-82 bg-white/20 rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          key="financial-modal"
          className="fixed inset-0 z-5001 flex  items-center justify-center backdrop-blur-[20px] bg-(--primary) "
        >
          <div className="w-full h-screen flex items-center justify-center px-4">
            <div className="relative z-50 w-full max-w-6xl max-h-auto flex flex-col">
              <div className="absolute -top-16 right-4 flex items-center gap-4 z-60">
                <button
                  onClick={() => dispatch(openHelpModal())}
                  className="px-4 py-2 hidden md:block rounded-full border over border-white/40 text-white text-sm backdrop-blur-md hover:bg-white/10 transition"
                >
                  Need Help?
                </button>

                <button
                  onClick={onClose}
                  className="w-10 h-10 hidden  md:flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition"
                >
                  <Icon.crossIcon className="text-white w-4 h-4" />
                </button>
              </div>
              <motion.div
                className="relative flex flex-col md:flex-row gap-6 2md:gap-20 bg-transparent max-h-auto  rounded-8xl items-stretch"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
              >
                <div className="w-full md:w-125.75 p-6 hidden md:block">
                  <div className=" text-2xl font-semibold text-white mb-4 pb-2 border-b-2 border-dashed border-white">
                    Client Assessment
                  </div>
                  <div className="relative">
                    {Object.entries(groupedQuestions).map(
                      ([stepperTitle], groupIndex) => {
                        const stepNumber = groupIndex + 1;
                        const active = currentStep === stepNumber;
                        const done = currentStep > stepNumber;

                        return (
                          <button
                            key={stepperTitle}
                            onClick={() => {
                              if (
                                isStepCompleted(
                                  groupedQuestions[stepperTitle],
                                ) ||
                                active
                              ) {
                                setCurrentStep(stepNumber);
                              }
                            }}
                            className={`flex w-full items-center justify-between py-3 px-4 mb-2 rounded-lg transition ${
                              active
                                ? "bg-white/10 text-white font-semibold"
                                : done
                                  ? "opacity-80 hover:bg-white/5 cursor-pointer"
                                  : "opacity-40 cursor-not-allowed"
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div
                                className={`w-8 h-8 flex items-center text-md justify-center rounded-full border-2 shrink-0 ${
                                  active
                                    ? "bg-white text-(--primary) border-white"
                                    : done
                                      ? "bg-[#1EC700] border-[#1EC700] text-white"
                                      : "border-gray-400 text-white"
                                }`}
                              >
                                {done ? "✓" : stepNumber}
                              </div>
                              <p className="text-white flex text-left items-center text-md">
                                {stepperTitle}
                              </p>
                            </div>
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>

                <div className="w-full flex flex-col items-center   ">
                  <div className="md:hidden w-full mb-4 relative">
                    <div className="flex items-cente justify-center gap-3">
                      <button
                        onClick={() => setShowSteps((prev) => !prev)}
                        className="flex-1 relative h-8 bg-white/80 rounded-full overflow-hidden shadow-sm"
                      >
                        {/* PROGRESS FILL */}
                        <div
                          className="absolute left-0 top-0 h-full bg-green-400 transition-all duration-300"
                          style={{
                            width: `${(currentStep / totalSteps) * 100}%`,
                          }}
                        />

                        {/* CONTENT INSIDE BAR */}
                        <div className="relative z-10 flex items-center justify-between h-full px-3 gap-2">
                          {/* STEP PILL */}
                          <div className="text-sm font-semibold py-0.5 rounded-full flex items-center min-w-0 flex-1">
                            <span className="text-[#023280]">
                              {currentStep}
                            </span>
                            <span className="text-gray-500">/</span>
                            <span className="text-black">{totalSteps}</span>
                            <span className="text-black mx-1">-</span>

                            <p className="text-xs xs:text-sm font-bold text-black truncate min-w-0">
                              {currentStepperTitle}
                            </p>
                          </div>

                          <Icon.dropdownDownArrow
                            className={`w-3 h-3 transition shrink-0 ${showSteps ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>

                      {/* CLOSE BUTTON */}
                      <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20"
                      >
                        <Icon.crossIcon className="text-white w-3 h-3" />
                      </button>
                    </div>

                    <AnimatePresence>
                      {showSteps && (
                        <motion.div
                          key="steps-dropdown"
                          initial={{ opacity: 0, y: -10, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.97 }}
                          transition={{
                            duration: 0.22,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className="absolute top-12 left-0 w-full bg-white rounded-xl shadow-lg z-50 overflow-hidden pt-5 px-5"
                        >
                          <h2 className="flex justify-center text-center mt-2 mb-5 font-bold text-xl items-center gap-2">
                            <div className="border border-[rgb(var(--primary-alpha)/0.3)] p-2 rounded-2lg">
                              <Icon.mysipLogoIconv1 />
                            </div>
                            Create a MF Portfolio
                          </h2>
                          <div className="p-4 bg-[#F1F4F8] border border-[rgb(var(--primary-alpha)/0.15)] rounded-xl">
                            {Object.entries(groupedQuestions).map(
                              ([title], index) => {
                                const stepNumber = index + 1;
                                const active = currentStep === stepNumber;

                                return (
                                  <button
                                    key={title}
                                    onClick={() => {
                                      if (
                                        isStepCompleted(
                                          groupedQuestions[title],
                                        ) ||
                                        active
                                      ) {
                                        setCurrentStep(stepNumber);
                                        setShowSteps(false);
                                      }
                                    }}
                                    className={`w-full flex text-sm items-center justify-between mx-auto px-4 py-3 border border-[rgb(var(--primary-alpha)/0.25)] m-2 rounded-xl gap-2 transition-all ${active ? "bg-blue-50 font-semibold text-(--primary) " : "text-[#404040] bg-white"} ${isStepCompleted(groupedQuestions[title]) ? "cursor-pointer hover:border-(--primary) " : "cursor-not-allowed"}`}
                                  >
                                    <div className="flex items-center gap-3 text-left text-xs md:text-base">
                                      <span
                                        className={`md:w-6 md:h-6 w-5 h-5 flex items-center justify-center rounded-full border ${
                                          isStepCompleted(
                                            groupedQuestions[title],
                                          )
                                            ? "bg-[#25A271] border-[#25A271] text-white"
                                            : "border-gray-400 text-gray-400"
                                        }`}
                                      >
                                        {isStepCompleted(
                                          groupedQuestions[title],
                                        ) ? (
                                          <Icon.rightSignIcon className="w-3 h-3" />
                                        ) : (
                                          <div>{stepNumber}</div>
                                        )}
                                      </span>
                                      <span>{title}</span>
                                    </div>
                                    {isStepCompleted(groupedQuestions[title]) &&
                                      !active && (
                                        <Icon.editIcon className="w-4 h-4 text-(--primary) " />
                                      )}
                                  </button>
                                );
                              },
                            )}
                          </div>
                          <div className="bg-white">
                            <button
                              onClick={() => setShowSteps((prev) => !prev)}
                              className="flex mx-auto p-1.5 m-2 border rounded-full bg-[#F1F4F8] border-(--primary)"
                            >
                              <motion.div
                                animate={{ rotate: showSteps ? 180 : 0 }}
                                transition={{
                                  duration: 0.25,
                                  ease: "easeInOut",
                                }}
                              >
                                <Icon.dropdownDownArrow className="w-4 h-4" />
                              </motion.div>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div
                    key={currentStep}
                    className="relative z-40 bg-white rounded-2xl md:rounded-7xl flex flex-col w-full  mx-auto overflow-hidden"
                  >
                    <motion.div
                      initial={{ x: "100%", opacity: 0 }}
                      animate={{
                        x: 0,
                        opacity: 1,
                        transition: {
                          duration: 0.45,
                          ease: [0.22, 1, 0.36, 1],
                        },
                      }}
                      exit={{
                        x: "100%",
                        opacity: 0,
                        transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
                      }}
                      className={` flex flex-col w-full items-center  rounded-tl-4xl rounded-tr-4xl  relative`}
                    >
                      <div
                        ref={scrollRef}
                        onScroll={handleScroll}
                        className="w-full h-auto max-h-136 md:min-h-162.5 px-5 py-6 lg:p-6 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] md:thin-scrollbar overflow-y-auto border-b"
                      >
                        {visibleQuestions.map((q, index) => {
                          const firstUnansweredIndex = activeQuestionIndex;

                          const isAnswered =
                            !!answers[q.id] ||
                            (q.field_type === "amount" &&
                              !!answers[q.id]?.answer?.toString().trim());

                          const isCurrent =
                            index === firstUnansweredIndex ||
                            openDropdown === q.id;

                          const isDimmed =
                            !isAnswered &&
                            index > firstUnansweredIndex &&
                            !isCurrent;

                          return (
                            <div
                              key={q.id}
                              ref={(el) => {
                                questionRefs.current[q.id] = el;
                              }}
                              className={`transition-all duration-300 w-full
                              
                              `}
                            >
                              <div
                                className={`group rounded-2xl 2md:px-4 pb-4 2md:pt-2 transition-all duration-300 ${
                                  isAnswered || isCurrent
                                    ? "opacity-100"
                                    : isDimmed
                                      ? "opacity-40"
                                      : "opacity-100"
                                }`}
                              >
                                {/* Question label */}
                                <div
                                  className={`text-primaryBlack/50 border-t border-gray-300 border-dashed md:text-base text-sm ${
                                    index === 0 ? "border-t-0 pt-0" : "pt-4"
                                  }`}
                                >
                                  <div className="flex">
                                    <span className="text-gray-600 pr-0.5">
                                      Question
                                    </span>
                                    <span className="text-(--primary) pr-0.5">
                                      {index + 1}
                                    </span>
                                    <span className="text-gray-600 pr-0.5">
                                      of
                                    </span>
                                    <span className="text-black pr-0.5">
                                      {visibleQuestions.length}
                                    </span>
                                  </div>
                                </div>

                                {/* Question text */}
                                <h2 className="text-base leading-1.2 md:text-xl md:leading-7 font-semibold mb-3 mt-2">
                                  {q.question}
                                </h2>

                                {q.field_type === "amount" &&
                                  q.id !== 143 &&
                                  amountErrors &&
                                  errorQuestions.has(q.id) && (
                                    <p className="text-red-500 text-xs mb-2 -mt-1">
                                      ⚠ Please answer this question
                                    </p>
                                  )}
                                {/* Error */}
                                {q.field_type === "amount" &&
                                  q.id !== 143 &&
                                  !amountErrors &&
                                  errorQuestions.has(q.id) && (
                                    <p className="text-red-500 text-xs mb-2 -mt-1">
                                      ⚠ Please answer this question
                                    </p>
                                  )}
                                {q.field_type !== "amount" &&
                                  q.id !== 143 &&
                                  errorQuestions.has(q.id) && (
                                    <p className="text-red-500 text-xs mb-2 -mt-1">
                                      ⚠ Please answer this question
                                    </p>
                                  )}

                                {/* Options — select */}
                                {q.field_type === "select" && (
                                  <div className="relative w-full">
                                    <button
                                      ref={(el) => {
                                        buttonRefs.current[q.id] = el;
                                      }}
                                      type="button"
                                      className={`w-full border p-3.5 rounded-xl text-left hover:border-primaryBlue bg-white flex justify-between items-center transition-all ${
                                        errorQuestions.has(q.id)
                                          ? "border-red-500"
                                          : openDropdown === q.id
                                            ? "border-(--primary) ring-2 ring-[rgb(var(--primary-alpha)/0.1)]"
                                            : "border-gray-300"
                                      }`}
                                      onClick={() =>
                                        setOpenDropdown((prev) =>
                                          prev === q.id ? null : q.id,
                                        )
                                      }
                                    >
                                      <p
                                        className={`text-xs md:text-sm ${answers[q.id]?.answer ? "text-gray-900" : "text-gray-400"}`}
                                      >
                                        {answers[q.id]?.answer ||
                                          "Choose an Option"}
                                      </p>
                                      <Icon.dropdownDownArrow
                                        className={`shrink-0 transition duration-300 ${
                                          openDropdown === q.id
                                            ? "rotate-180 text-primaryGreen"
                                            : "text-[#006b66a4]"
                                        }`}
                                      />
                                    </button>

                                    <CustomDropdown
                                      excludeRefs={[
                                        { current: buttonRefs.current[q.id] },
                                      ]}
                                      isOpen={openDropdown === q.id}
                                      onClose={() => setOpenDropdown(null)}
                                      className="w-full mt-1 rounded-lg shadow-lg z-50"
                                    >
                                      <div className="flex flex-col">
                                        {q.options.map(
                                          (opt: PortfolioApiOption) => {
                                            const isSelected =
                                              answers[q.id]?.option_id ===
                                              opt.id;
                                            return (
                                              <button
                                                key={opt.id}
                                                type="button"
                                                className={`px-4 py-3 text-left text-xs md:text-sm flex items-center justify-between transition-colors ${
                                                  isSelected
                                                    ? "bg-[rgb(var(--primary-alpha)/0.1)] text-(--primary) font-medium"
                                                    : "hover:bg-gray-100 text-gray-700"
                                                }`}
                                                onClick={() => {
                                                  setAnswers((prev) => ({
                                                    ...prev,
                                                    [q.id]: {
                                                      question_id: q.id,
                                                      option_id: opt.id,
                                                      answer: opt.option,
                                                    },
                                                  }));
                                                  setOpenDropdown(null);
                                                  setActiveQuestionIndex(
                                                    index + 1,
                                                  );
                                                  setTimeout(() => {
                                                    scrollToNextUnanswered(
                                                      index,
                                                      q.id,
                                                    );
                                                  }, 50);
                                                  setErrorQuestions((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(q.id);
                                                    return next;
                                                  });
                                                }}
                                              >
                                                {opt.option}
                                                {isSelected && (
                                                  <div className="w-2 h-2 rounded-full bg-(--primary) shrink-0" />
                                                )}
                                              </button>
                                            );
                                          },
                                        )}
                                      </div>
                                    </CustomDropdown>
                                  </div>
                                )}

                                {/* Options — radio */}
                                {q.field_type === "radio" && (
                                  <div className="flex flex-col gap-4">
                                    {q.options.map(
                                      (opt: PortfolioApiOption) => {
                                        const isSelected =
                                          answers[q.id]?.option_id === opt.id;
                                        return (
                                          <label
                                            key={opt.id}
                                            className={`flex w-full items-center gap-2 px-2 py-3 rounded-xl border cursor-pointer transition-all ${
                                              isDimmed
                                                ? "opacity-90 hover:border hover:border-(--primary) "
                                                : ""
                                            } ${
                                              errorQuestions.has(q.id)
                                                ? "border-red-500"
                                                : ""
                                            } ${isSelected ? "bg-[rgb(var(--primary-alpha)/0.1)] border-(--primary) " : "bg-white border-gray-300 hover:border-[rgb(var(--primary-alpha)/0.5)] hover:bg-blue-50/40"}`}
                                          >
                                            <input
                                              type={q.field_type}
                                              name={`question-${q.id}`}
                                              value={opt.option}
                                              checked={isSelected}
                                              onChange={() => {
                                                setAnswers((prev) => {
                                                  const next = { ...prev };
                                                  next[q.id] = {
                                                    question_id: q.id,
                                                    option_id: opt.id,
                                                    answer: opt.option,
                                                  };
                                                  if (
                                                    q.id ===
                                                    INVESTMENT_TYPE_Q_ID
                                                  ) {
                                                    delete next[SIP_Q_ID];
                                                    delete next[LUMPSUM_Q_ID];
                                                  }
                                                  return next;
                                                });
                                                // ⬇ rest stays exactly as-is
                                                setActiveQuestionIndex(
                                                  index + 1,
                                                );
                                                scrollToNextUnanswered(
                                                  index,
                                                  q.id,
                                                );
                                                setErrorQuestions((prev) => {
                                                  const next = new Set(prev);
                                                  next.delete(q.id);
                                                  return next;
                                                });
                                              }}
                                              className="hidden"
                                            />
                                            <div
                                              className={`w-3 h-3 shrink-0 md:w-4 md:h-4 rounded-full border flex items-center justify-center ${
                                                isSelected
                                                  ? "border-(--primary) "
                                                  : "border-gray-500"
                                              }`}
                                            >
                                              {isSelected && (
                                                <div className="w-2 h-2 bg-(--primary)  rounded-full" />
                                              )}
                                            </div>
                                            <span className="text-xs md:text-sm font-medium text-gray-700">
                                              {opt.option}
                                            </span>
                                          </label>
                                        );
                                      },
                                    )}
                                  </div>
                                )}

                                {/* Options — radio box */}
                                {q.field_type === "radio box" && (
                                  <div className="flex flex-col gap-4">
                                    {q.options.map(
                                      (opt: PortfolioApiOption) => {
                                        const isSelected =
                                          answers[q.id]?.option_id === opt.id;
                                        return (
                                          <label
                                            key={opt.id}
                                            className={`flex w-full items-center gap-2 px-2 py-3 rounded-xl border cursor-pointer${
                                              isDimmed
                                                ? "opacity-90 hover:border hover:border-(--primary) cursor-pointer"
                                                : ""
                                            } transition-all ${
                                              errorQuestions.has(q.id)
                                                ? "border-red-500"
                                                : ""
                                            } ${isSelected ? "bg-[rgb(var(--primary-alpha)/0.1)] border-(--primary) " : "bg-white border-gray-300 hover:border-[rgb(var(--primary-alpha)/0.5)] hover:bg-blue-50/40"}`}
                                          >
                                            <input
                                              type="radio"
                                              name={`question-${q.id}`}
                                              value={opt.option}
                                              checked={isSelected}
                                              onChange={() => {
                                                setAnswers((prev) => ({
                                                  ...prev,
                                                  [q.id]: {
                                                    question_id: q.id,
                                                    option_id: opt.id,
                                                    answer: opt.option,
                                                  },
                                                }));
                                                scrollToNextUnanswered(
                                                  index,
                                                  q.id,
                                                );
                                                setActiveQuestionIndex(
                                                  index + 1,
                                                );
                                                setErrorQuestions((prev) => {
                                                  const next = new Set(prev);
                                                  next.delete(q.id);
                                                  return next;
                                                });
                                              }}
                                              className="hidden"
                                            />
                                            <div
                                              className={`w-4 h-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
                                                isSelected
                                                  ? "border-(--primary) "
                                                  : "border-gray-400"
                                              }`}
                                            >
                                              {isSelected && (
                                                <div className="w-2 h-2 bg-(--primary)  rounded-full" />
                                              )}
                                            </div>
                                            {opt.logo && (
                                              <Image
                                                src={opt.logo}
                                                alt={
                                                  opt.description || opt.option
                                                }
                                                width={60}
                                                height={60}
                                                className="w-9 h-9 shrink-0 rounded-lg object-cover"
                                              />
                                            )}
                                            <div className="flex flex-col">
                                              <span className="text-xs md:text-sm font-semibold text-gray-800">
                                                {opt.option}
                                              </span>
                                            </div>
                                          </label>
                                        );
                                      },
                                    )}
                                  </div>
                                )}

                                {/* Options — amount input */}
                                {q.field_type === "amount" && (
                                  <div className="flex flex-col gap-1">
                                    <div
                                      className={`flex items-center border rounded-xl overflow-hidden transition-all ${
                                        errorQuestions.has(q.id) ||
                                        amountErrors[q.id]
                                          ? "border-red-500"
                                          : "border-gray-300 focus-within:border-(--primary) focus-within:ring-2 focus-within:ring-[rgb(var(--primary-alpha)/0.1)]"
                                      }`}
                                    >
                                      <span className="px-3 py-3.5 bg-gray-50 border-r border-gray-300 text-gray-500 text-sm select-none">
                                        ₹
                                      </span>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="Enter amount"
                                        value={
                                          answers[q.id]?.answer
                                            ? Number(
                                                answers[q.id].answer,
                                              ).toLocaleString("en-IN")
                                            : 0 // ← empty string, not 0
                                        }
                                        onChange={(e) => {
                                          const raw = e.target.value.replace(
                                            /,/g,
                                            "",
                                          );
                                          if (raw !== "" && !/^\d+$/.test(raw))
                                            return; // ← allow only digits

                                          setAnswers((prev) => ({
                                            ...prev,
                                            [q.id]: {
                                              question_id: q.id,
                                              option_id: 0,
                                              answer: raw,
                                            },
                                          }));

                                          setAmountErrors((prev) => {
                                            const next = { ...prev };
                                            delete next[q.id];
                                            return next;
                                          });

                                          if (raw) {
                                            setErrorQuestions((prev) => {
                                              const next = new Set(prev);
                                              next.delete(q.id);
                                              return next;
                                            });
                                          }
                                        }}
                                        onBlur={() => {
                                          const val = answers[q.id]?.answer;
                                          const num = Number(val);

                                          const minAmount =
                                            q.id === 141
                                              ? 5000
                                              : q.id === 142
                                                ? 100000
                                                : 0;
                                          const minLabel =
                                            q.id === 141
                                              ? "₹5,000"
                                              : q.id === 142
                                                ? "₹1,00,000"
                                                : null;

                                          if (!val || num <= 0) return;

                                          if (minLabel && num < minAmount) {
                                            setAmountErrors((prev) => ({
                                              ...prev,
                                              [q.id]: `Minimum amount is ${minLabel}`,
                                            }));
                                            return;
                                          }

                                          setActiveQuestionIndex(index + 1);
                                          scrollToNextUnanswered(index, q.id);
                                        }}
                                        className="flex-1 px-3 py-3.5 text-sm text-gray-800 bg-white outline-hidden"
                                      />
                                    </div>

                                    {/* Amount error message */}
                                    {amountErrors[q.id] &&
                                      !errorQuestions.has(q.id) && (
                                        <p className="text-red-500 text-xs mt-0.5">
                                          ⚠ {amountErrors[q.id]}
                                        </p>
                                      )}

                                    {/* Min amount hint */}
                                    {!nextStep &&
                                      !amountErrors[q.id] &&
                                      !errorQuestions.has(q.id) && (
                                        <p className="text-gray-400 text-xs mt-0.5">
                                          {q.id === 141
                                            ? "Minimum: ₹5,000"
                                            : q.id === 142
                                              ? "Minimum: ₹1,00,000"
                                              : ""}
                                        </p>
                                      )}
                                    {amountErrors[q.id] &&
                                      errorQuestions.has(q.id) && (
                                        <p className="text-gray-400 text-xs mt-0.5">
                                          {q.id === 141
                                            ? "Minimum: ₹5,000"
                                            : q.id === 142
                                              ? "Minimum: ₹1,00,000"
                                              : ""}
                                        </p>
                                      )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  </div>{" "}
                  {isDesktop && !atBottom && (
                    <div className="absolute bottom-14 bg-white/50 left-7 xxx:left-9 xxs:left-10 xs:left-14 sm:left-18 right-0 h-8 w-5/6 pointer-events-none rounded-b-8xl" />
                  )}
                  <div className="w-full flex justify-center mt-6">
                    <div className="flex justify-center gap-4 w-full max-w-xl ">
                      <button
                        onClick={prevStep}
                        className={`${currentStep === 1 ? "hidden" : "flex"} items-center gap-1.5 px-4 2md:px-8 py-2 rounded-full border border-white bg-white 2md:bg-transparent text-(--primary) 2md:text-white transition-colors`}
                      >
                        <svg
                          className="w-4 h-6  block rotate-180"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M3 12H17M11 5L19 12L11 19"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="hidden 2md:block">Back</span>
                      </button>

                      <button
                        onClick={nextStep}
                        disabled={isButtonDisabled}
                        className={`flex px-2 min-w-200 2md:px-3 2md:min-w-82 py-2 justify-center rounded-full font-semibold transition-all
    ${
      isButtonDisabled
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-white text-(--primary) hover:bg-gray-100"
    }
  `}
                      >
                        {loading ? (
                          <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : currentStep === totalSteps ? (
                          "Submit"
                        ) : (
                          "Continue"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioModel;
